/**
 * Cover Letter store — persists generated cover letters to localStorage.
 * Each letter is linked to a pipeline job via jobId.
 * In cloud mode: fetches from backend on first load.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GeneratedCoverLetter } from '@shared/types/coverLetter';
import { useAuthStore } from './auth.store';
import { coverLetterService } from '@/services/coverLetter.service';

export interface StoredCoverLetter extends GeneratedCoverLetter {
  /** Pipeline job ID this letter was generated for */
  jobId: string;
  /** Job title for display in Documents page */
  jobTitle?: string;
  /** Company name for display */
  company?: string;
}

interface CoverLetterState {
  coverLetters: StoredCoverLetter[];
  hasFetchedFromBackend: boolean;
}

interface CoverLetterActions {
  addCoverLetter: (letter: StoredCoverLetter) => void;
  removeCoverLetter: (id: string) => void;
  getCoverLettersByJobId: (jobId: string) => StoredCoverLetter[];
  getCoverLetterById: (id: string) => StoredCoverLetter | undefined;
  fetchFromBackend: () => Promise<void>;
}

export type CoverLetterStore = CoverLetterState & CoverLetterActions;

export const useCoverLetterStore = create<CoverLetterStore>()(
  persist(
    (set, get) => ({
      coverLetters: [],
      hasFetchedFromBackend: false,

      /**
       * Fetch cover letters from backend (cloud mode only).
       * Merges backend data with any local-only letters.
       */
      fetchFromBackend: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;

        try {
          const backendLetters = await coverLetterService.listCoverLetters();

          // Map backend format to StoredCoverLetter
          const clList = Array.isArray(backendLetters) ? backendLetters : [];
          const mapped: StoredCoverLetter[] = clList.map((cl) => ({
            id: cl.id,
            generatedAt: cl.generatedAt,
            content: cl.content,
            letterText: cl.letterText,
            template: cl.template,
            wordCount: cl.wordCount,
            atsKeywordCoverage: cl.atsKeywordCoverage,
            jobId: cl.vacancyId || '',
            jobTitle: cl.jobTitle,
            company: cl.company,
          }));

          // Merge: backend letters + any local letters not yet on backend
          const backendIds = new Set(mapped.map((cl) => cl.id));
          const localOnly = get().coverLetters.filter((cl) => !backendIds.has(cl.id));

          set({
            coverLetters: [...mapped, ...localOnly],
            hasFetchedFromBackend: true,
          });
        } catch (error) {
          console.error('Failed to fetch cover letters from backend:', error);
        }
      },

      addCoverLetter: (letter) => {
        set((state) => ({
          coverLetters: [letter, ...state.coverLetters],
        }));
      },

      removeCoverLetter: (id) => {
        set((state) => ({
          coverLetters: state.coverLetters.filter((cl) => cl.id !== id),
        }));
      },

      getCoverLettersByJobId: (jobId) => {
        return get().coverLetters.filter((cl) => cl.jobId === jobId);
      },

      getCoverLetterById: (id) => {
        return get().coverLetters.find((cl) => cl.id === id);
      },
    }),
    {
      name: 'cover-letter-storage',
      partialize: (state) => ({
        coverLetters: state.coverLetters,
      }),
    }
  )
);
